import { Button as BaseButton } from "@base-ui/react/button";
import { Toggle as BaseToggle } from "@base-ui/react/toggle";
import type { IconData, IconStyle } from "@loot-os/icons/types";
import type { ComponentProps } from "react";
import { Icon } from "./Icon.js";
import { iconButtonStyles } from "./IconButton.styles.js";

type Variants = NonNullable<Parameters<typeof iconButtonStyles>[0]>;

interface IconButtonOwnProps {
  icon: IconData;
  /** The accessible name; the button shows no text. Keep it the same when `pressed` changes. */
  label: string;
  variant?: Variants["variant"];
  size?: Variants["size"];
  className?: string;
  /** Plays the icon each time this value changes: something happened (`Icon`'s `playOn`). */
  playOn?: unknown;
  /** Draws the icon in this style instead of the theme's (`Icon`'s `variant`). */
  iconVariant?: IconStyle;
}

export type IconButtonProps = IconButtonOwnProps &
  (
    | (Omit<ComponentProps<typeof BaseButton>, "className" | "children"> & {
        pressed?: never;
        onPressedChange?: never;
      })
    | (Omit<ComponentProps<typeof BaseToggle>, "className" | "children" | "pressed"> & {
        /** Makes it an on/off button (`aria-pressed`): Mute, Fast open. */
        pressed: boolean;
      })
  );

/** An action with only an icon: close, back, theme, chat. */
export function IconButton({
  icon,
  label,
  variant,
  size,
  className,
  playOn,
  iconVariant,
  ...props
}: IconButtonProps) {
  // An icon-only control: its icon plays on hover where the theme animates every icon.
  const glyph = (
    <Icon
      icon={icon}
      size={size === "sm" || size === "xs" ? 16 : 20}
      trigger="hover"
      playOn={playOn}
      variant={iconVariant}
    />
  );
  if (props.pressed !== undefined) {
    return (
      <BaseToggle
        aria-label={label}
        className={iconButtonStyles({ variant, size, toggle: true, className })}
        {...(props as ComponentProps<typeof BaseToggle>)}
      >
        {glyph}
      </BaseToggle>
    );
  }
  const { pressed: _pressed, onPressedChange: _change, ...buttonProps } = props;
  return (
    <BaseButton
      focusableWhenDisabled
      aria-label={label}
      className={iconButtonStyles({ variant, size, className })}
      {...buttonProps}
      // Rendered through another element (`render`), it isn't a native <button> unless told so.
      nativeButton={buttonProps.nativeButton ?? buttonProps.render === undefined}
    >
      {glyph}
    </BaseButton>
  );
}
